import { useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutocomplete';

export function PlacesAutocompleteInput({
  label,
  placeholder,
  value,
  onSelect,
  onInputChange,
  icon = <MapPin className="h-4 w-4" />,
  disabled,
}) {
  const [inputValue, setInputValue] = useState(value?.description ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const {
    ready,
    error,
    predictions,
    loading,
    fetchPredictions,
    clearPredictions,
    getPlaceDetails,
  } = usePlacesAutocomplete();

  useEffect(() => {
    setInputValue(value?.description ?? '');
  }, [value?.description]);

  const showSuggestions = useMemo(
    () => isFocused && predictions.length > 0,
    [isFocused, predictions.length],
  );

  const wrapperZIndex = showSuggestions ? 'z-50' : 'z-30';

  return (
    <div className={`relative ${wrapperZIndex} w-full text-left`}>
      <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">
        {label}
      </label>
      <div
        className={`mt-2 flex w-full items-center gap-3 rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-800 px-4 py-3 text-lg transition focus-within:border-black dark:focus-within:border-yellow-500 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        {icon}
        <input
          className="flex-1 bg-transparent text-base text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none"
          placeholder={placeholder}
          value={inputValue}
          disabled={!ready || disabled}
          onChange={event => {
            const nextValue = event.target.value;
            setInputValue(nextValue);
            onInputChange?.(nextValue);
            if (nextValue.length > 2) {
              fetchPredictions(nextValue);
            } else {
              clearPredictions();
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setTimeout(() => setIsFocused(false), 150);
          }}
        />
        {loading && (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400 dark:text-slate-500" />
        )}
      </div>
      {showSuggestions && (
        <div className="absolute inset-x-0 top-[calc(100%+0.5rem)] z-40 overflow-hidden rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl">
          {predictions.map(prediction => (
            <button
              key={prediction.place_id}
              type="button"
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
              onClick={async () => {
                const details = await getPlaceDetails(prediction.place_id);
                onSelect({
                  description: details.description || prediction.description,
                  placeId: prediction.place_id,
                  latitude: details.latitude,
                  longitude: details.longitude,
                });
                onInputChange?.(
                  details.description || prediction.description || '',
                );
                setInputValue(
                  details.description || prediction.description || '',
                );
                clearPredictions();
              }}
            >
              <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-slate-500" />
              <span>{prediction.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
